import type { ComponentProps, FC } from 'react';
import React, { memo } from 'react';
import { graphql } from 'react-relay';
import { Subscribe } from 'unstated';

import { Box } from '@atlaskit/primitives/compiled/box';

import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { RendererContentState } from '@confluence/content-state/entry-points/RendererContentState.ts';
import { EditorContentState } from '@confluence/content-state/entry-points/EditorContentState.ts';
import { ContentStateStateContainer } from '@confluence/content-state/entry-points/ContentStateStateContainer.ts';
import { fg } from '@confluence/feature-gating';
import { BadgeStylePageOwnershipByline } from '@confluence/content-ownership/entry-points/BadgeStylePageOwnershipByline.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey.ts';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';
import { LivePagesByLineReactions } from '@confluence/live-pages-features/entry-points/LivePagesByLineReactions.ts';
import { AnalyticsByLine } from '@confluence/confluence-analytics/entry-points/AnalyticsByLine.ts';
import { ReadTime } from '@confluence/read-time/entry-points/ReadTime.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { ListenByline } from '@confluence/audio/entry-points/ListenByline.ts';
import { useAudioFeatureFlags } from '@confluence/audio/entry-points/useAudioFeatureFlags.ts';
import { BylineConnections } from '@confluence/byline-connections/entry-points/BylineConnections.ts';
import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';

import type { UserShape } from './byLineTypes.ts';
import { BylineClassification } from './BylineClassification.tsx';
import { SingleLineByLine } from './SingleLineByLine.tsx';
import { LazyAIPlannerSyncByline } from './ai-planner-sync/AIPlannerSyncBylineLoader.tsx';
import { AI_PLANNER_GENERATED_BY } from './ai-planner-sync/aiPlannerSyncBylineConfig.ts';
import type {
	LivePagesByLineWithContributorsComponentEditorContentStateQuery$data,
	LivePagesByLineWithContributorsComponentEditorContentStateQuery$variables,
} from './__generated__/LivePagesByLineWithContributorsComponentEditorContentStateQuery.graphql.ts';
import type { ContentPropertyConnection } from './validateContentPropertyData.tsx';

export type LivePagesByLineWithContributorProps = {
	contentId: string;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	author: UserShape;
	owner: UserShape | null;
	contributorsCount: number;
	isOwnerContributor: boolean;
	contentType: string;
	isPublishingContent: boolean;
	userOffline: boolean;
	isKnowledgeBaseSpace: boolean;
	viewMode?: boolean;
	editorApi?: FullPageEditorPresetAPI | null;
	isUnpublishedDraft?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
	hasAudioByline?: boolean;
	generatedBy?: string | null;
	spaceName?: string | null;
	classificationContextFragmentRef?: ComponentProps<
		typeof BylineClassification
	>['classificationContextFragmentRef'];
	bylineAppContentProperties?: ContentPropertyConnection | null;
};

export const LivePagesByLineWithContributorsComponent: FC<LivePagesByLineWithContributorProps> =
	memo(
		({
			contentId,
			pageVersion,
			pageUpdatedAt,
			author,
			owner,
			contributorsCount,
			isOwnerContributor,
			contentType,
			isPublishingContent,
			userOffline,
			isKnowledgeBaseSpace,
			viewMode,
			editorApi,
			isUnpublishedDraft,
			hasByLineContributors,
			hasByLineExtensions,
			hasReactions,
			hasAudioByline = true,
			generatedBy,
			spaceName,
			classificationContextFragmentRef,
			bylineAppContentProperties,
		}) => {
			const isLivePage = useIsCurrentPageLive();
			const spaceId = useSpaceId();
			const [spaceKey] = usePageSpaceKey();
			const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
			const isRovoSystemSpace = isRovoSystemSpaceKey(spaceKey);
			const isMarkdownMode = useIsMarkdownMode(isLivePage ? contentId : undefined);

			const { approvalRequest, availableWorkflowStatuses } = useApprovals();
			const hasActiveWorkflowStatus =
				!!approvalRequest?.application?.workflowStatusId &&
				availableWorkflowStatuses.some(
					(s) => s.id === approvalRequest.application.workflowStatusId,
				) &&
				expValEquals('confluence_approvals_eap', 'isEnabled', true);

			const { isAudioBylineEntrypointEnabled } = useAudioFeatureFlags();

			const { data } = useQueryInterop<
				LivePagesByLineWithContributorsComponentEditorContentStateQuery$data,
				LivePagesByLineWithContributorsComponentEditorContentStateQuery$variables
			>(
				graphql`
					query LivePagesByLineWithContributorsComponentEditorContentStateQuery(
						$contentId: ID!
						$isDraft: Boolean!
					) {
						singleContent(id: $contentId) {
							id
							contentState(isDraft: $isDraft) {
								id
								name
								color
								restrictionLevel
								isCallerPermitted
							}
							# currently only view page and live pages require this field to be loaded for the content header
							contentStateLastUpdated(format: USER_FRIENDLY) @skip(if: $isDraft) {
								value
							}
						}
						availableContentStates(contentId: $contentId) {
							customContentStates {
								name
								color
								id
							}
							spaceContentStates {
								name
								color
								id
								restrictionLevel
							}
						}
					}
				`,
				{
					fetchPolicy: 'cache-first',
					variables: {
						contentId,
						isDraft: true,
					},
					skip: !window.__SSR_RENDERED__,
				},
			);

			const contributorCountWithoutOwner = contributorsCount - (isOwnerContributor ? 1 : 0);

			const eligibleForReactions = isEmbeddedConfluence ? hasReactions : true;
			const shouldHideReactionsInRovoStaging =
				isRovoSystemSpace &&
				generatedBy === AI_PLANNER_GENERATED_BY &&
				fg('confluence-ai-planner-hide-reaction');
			const renderReactions =
				(isLivePage || !isUnpublishedDraft) &&
				eligibleForReactions &&
				!shouldHideReactionsInRovoStaging;

			// reactions are larger than the owner item, so if we arent rending reactions (example: unpublished drafts), we need to add extra padding to avoid the placeholder being too large and causing a jump on load
			// ideally we will get reactions down to the same size as other items (24px) so we can remove this extra padding
			const singleLineOwnershipByline = (
				<Box paddingBlock={renderReactions ? undefined : 'space.050'}>
					<BadgeStylePageOwnershipByline
						owner={owner}
						author={author}
						analyticsSource="liveDoc"
						contributorCountWithoutOwner={contributorCountWithoutOwner}
						generatedBy={generatedBy}
					/>
				</Box>
			);

			const shouldHideMarkdownModeOptions = isMarkdownMode;

			const showByLineExtensions = isEmbeddedConfluence ? hasByLineExtensions : true;
			const showThirdPartyByLineExtensions = showByLineExtensions && !shouldHideMarkdownModeOptions;
			// The embedded editor surfaces the plan through the docs tab instead.
			const shouldRenderAIPlannerSyncByline =
				generatedBy === AI_PLANNER_GENERATED_BY &&
				fg('confluence_ai_planner_sync_byline') &&
				!(isEmbeddedConfluence && fg('migrate_planner_to_docs_tab'));
			const byLineExtensions = (
				<>
					<BylineClassification
						contentId={contentId}
						classificationContextFragmentRef={classificationContextFragmentRef}
					/>
					<Subscribe to={[ContentStateStateContainer]}>
						{(contentStateState: ContentStateStateContainer) => {
							// view mode users should not be able to edit the content state, so we just render the content state.
							if (viewMode) {
								return (
									<RendererContentState
										contentId={contentId}
										hasSeparator={false}
										isDraft={!isLivePage}
									/>
								);
							}
							// Knowledge space live pages will always show the editable content state component.
							// Other live pages will only show the editable content state component if the content state is already set.
							// This is because a page without a content state is initialized by the ContentStateHoverButton component.
							// SSRd content state will have it's value in gql cache initially instead of the unstated store
							const isSSRRenderedOnLoad =
								Boolean(data?.singleContent?.contentState) &&
								Boolean(window.__SSR_RENDERED__) &&
								contentStateState.state.shouldRunContentStateEffect;
							if (
								contentStateState.state.contentState ||
								hasActiveWorkflowStatus ||
								isKnowledgeBaseSpace ||
								isSSRRenderedOnLoad
							) {
								return (
									<EditorContentState
										contentId={contentId}
										contentType={contentType}
										isPublishingContent={isPublishingContent}
										userOffline={userOffline}
										floatingToolbarButtonStyle={{
											alignItems: 'center',
											height: '24px',
										}}
										isViewMode={viewMode ?? false}
									/>
								);
							} else {
								return null;
							}
						}}
					</Subscribe>
					{!shouldHideMarkdownModeOptions && (
						<ReadTime contentId={contentId} editorApi={editorApi} isInEditor />
					)}
					{isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment') && (
						<BylineConnections
							contentId={contentId}
							pageVersion={pageVersion}
							pageUpdatedAt={pageUpdatedAt}
						/>
					)}
					{/* hasAudioByline is not part of the embedded-confluence-allowed-features API, so unlike
					the other has* props it applies regardless of isEmbeddedConfluence. */}
					{isAudioBylineEntrypointEnabled &&
						!shouldHideMarkdownModeOptions &&
						(hasAudioByline ||
							!fg('migrate_planner_to_docs_tab') ||
							!fg('confluence_ai_planner_sync_byline')) && (
							<ListenByline contentId={contentId} contentType={contentType} />
						)}
					{(isLivePage || !isUnpublishedDraft) && (
						<AnalyticsByLine contentId={contentId} contentAuthorId={author.userId} />
					)}
				</>
			);
			const ByLineCommonContent = (
				<>
					{showByLineExtensions && byLineExtensions}
					{renderReactions && (
						<LivePagesByLineReactions
							contentId={contentId}
							spaceId={spaceId || ''}
							isDisabled={userOffline}
							contentType={contentType}
						/>
					)}
				</>
			);
			const showByLineContributors = isEmbeddedConfluence ? hasByLineContributors : true;

			return (
				<SingleLineByLine
					contentId={contentId}
					contentType={contentType as 'page' | 'blogpost'}
					showByLineExtensions={showThirdPartyByLineExtensions}
					bylineAppContentProperties={bylineAppContentProperties}
				>
					{showByLineContributors && singleLineOwnershipByline}
					{shouldRenderAIPlannerSyncByline && (
						<LazyAIPlannerSyncByline
							contentId={contentId}
							contentType={contentType}
							generatedBy={generatedBy}
							isEmbeddedConfluence={isEmbeddedConfluence}
							spaceName={spaceName}
						/>
					)}
					{ByLineCommonContent}
				</SingleLineByLine>
			);
		},
	);
